import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy load views for better performance
const LoginView = () => import('@/views/auth/LoginView.vue')
const SignupView = () => import('@/views/auth/SignupView.vue')
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue')
const ResetPasswordView = () => import('@/views/auth/ResetPasswordView.vue')
const DashboardView = () => import('@/views/DashboardView.vue')
const PackagesView = () => import('@/views/PackagesView.vue')
const PackageDetailsView = () => import('@/views/PackageDetailsView.vue')
const VisaView = () => import('@/views/VisaView.vue')
const VisaDetailsView = () => import('@/views/VisaDetailsView.vue')
const TransportsView = () => import('@/views/TransportsView.vue')
const InvoicesView = () => import('@/views/InvoicesView.vue')
const InvoiceDetailsView = () => import('@/views/InvoiceDetailsView.vue')
const BookingVouchersView = () => import('@/views/BookingVouchersView.vue')
const BookingVoucherDetailsView = () => import('@/views/BookingVoucherDetailsView.vue')
const PackageRequestsView = () => import('@/views/PackageRequestsView.vue')
const PackageRequestDetailsView = () => import('@/views/PackageRequestDetailsView.vue')
const PackageRequestCreateView = () => import('@/views/PackageRequestCreateView.vue')
const CompanyProfileView = () => import('@/views/company/ProfileView.vue')
const CompanyEditView = () => import('@/views/company/EditView.vue')
const UsersView = () => import('@/views/users/UsersView.vue')
const UserCreateView = () => import('@/views/users/UserCreateView.vue')
const UserEditView = () => import('@/views/users/UserEditView.vue')
const ChangePasswordView = () => import('@/views/ChangePasswordView.vue')
const NotificationsView = () => import('@/views/NotificationsView.vue')
const ChatView = () => import('@/views/ChatView.vue')
const SearchView = () => import('@/views/SearchView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')

// Finance views
const FinanceDashboardView = () => import('@/views/finance/FinanceDashboardView.vue')
const LedgerListView = () => import('@/views/finance/LedgerListView.vue')
const PaymentHistoryView = () => import('@/views/finance/PaymentHistoryView.vue')
const RefundHistoryView = () => import('@/views/finance/RefundHistoryView.vue')
const BalanceRequestListView = () => import('@/views/finance/BalanceRequestListView.vue')
const BalanceRequestCreateView = () => import('@/views/finance/BalanceRequestCreateView.vue')
const BalanceReceiptView = () => import('@/views/finance/BalanceReceiptView.vue')
const RefundReceiptView = () => import('@/views/finance/RefundReceiptView.vue')

// Passport views
const PassportsView = () => import('@/views/PassportsView.vue')
const PassportDetailsView = () => import('@/views/PassportDetailsView.vue')

const routes: RouteRecordRaw[] = [
  // Public routes (no auth required)
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { guest: true, title: 'Login' }
  },
  {
    path: '/signup',
    name: 'signup',
    component: SignupView,
    meta: { guest: true, title: 'Sign Up' }
  },
  {
    path: '/password/reset',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: { guest: true, title: 'Forgot Password' }
  },
  {
    path: '/password/reset/:token',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: { guest: true, title: 'Set New Password' }
  },

  // Protected routes (auth required)
  {
    path: '/home',
    name: 'home',
    component: DashboardView,
    meta: { requiresAuth: true, title: 'Dashboard' }
  },
  {
    path: '/packages',
    name: 'packages',
    component: PackagesView,
    meta: { requiresAuth: true, title: 'Packages' }
  },
  {
    path: '/package/:id',
    name: 'package-details',
    component: PackageDetailsView,
    meta: { requiresAuth: true, title: 'Package Details' }
  },
  {
    path: '/visa',
    name: 'visa',
    component: VisaView,
    meta: { requiresAuth: true, title: 'Visa' }
  },
  {
    path: '/visa/:id',
    name: 'visa-details',
    component: VisaDetailsView,
    meta: { requiresAuth: true, title: 'Visa Details' }
  },
  {
    path: '/transports',
    name: 'transports',
    component: TransportsView,
    meta: { requiresAuth: true, title: 'Transports' }
  },
  {
    path: '/invoices',
    name: 'invoices',
    component: InvoicesView,
    meta: { requiresAuth: true, title: 'Invoices' }
  },
  {
    path: '/invoices/:type/:id',
    name: 'invoice-details',
    component: InvoiceDetailsView,
    meta: { requiresAuth: true, title: 'Invoice Details' }
  },
  {
    path: '/booking-vouchers',
    name: 'booking-vouchers',
    component: BookingVouchersView,
    meta: { requiresAuth: true, title: 'Booking Vouchers' }
  },
  {
    path: '/booking-vouchers/:id',
    name: 'booking-voucher-details',
    component: BookingVoucherDetailsView,
    meta: { requiresAuth: true, title: 'Booking Voucher Details' }
  },
  {
    path: '/package-requests',
    name: 'package-requests',
    component: PackageRequestsView,
    meta: { requiresAuth: true, title: 'Package Requests' }
  },
  {
    path: '/package-requests/create',
    name: 'package-request-create',
    component: PackageRequestCreateView,
    meta: { requiresAuth: true, title: 'Create Package Request' }
  },
  {
    path: '/package-requests/:id',
    name: 'package-request-details',
    component: PackageRequestDetailsView,
    meta: { requiresAuth: true, title: 'Package Request Details' }
  },

  // Finance routes
  {
    path: '/finance',
    name: 'finance',
    component: FinanceDashboardView,
    meta: { requiresAuth: true, title: 'Finance' }
  },
  {
    path: '/finance/ledger',
    name: 'finance-ledger',
    component: LedgerListView,
    meta: { requiresAuth: true, title: 'Company Ledger' }
  },
  {
    path: '/finance/payments',
    name: 'finance-payments',
    component: PaymentHistoryView,
    meta: { requiresAuth: true, title: 'Payment History' }
  },
  {
    path: '/finance/refunds',
    name: 'finance-refunds',
    component: RefundHistoryView,
    meta: { requiresAuth: true, title: 'Refund History' }
  },
  {
    path: '/finance/refunds/:id/receipt',
    name: 'finance-refund-receipt',
    component: RefundReceiptView,
    meta: { requiresAuth: true, title: 'Refund Receipt' }
  },
  {
    path: '/finance/balance-requests',
    name: 'finance-balance-requests',
    component: BalanceRequestListView,
    meta: { requiresAuth: true, title: 'Balance Requests' }
  },
  {
    path: '/finance/balance-requests/create',
    name: 'finance-balance-request-create',
    component: BalanceRequestCreateView,
    meta: { requiresAuth: true, title: 'Submit Balance Request' }
  },
  {
    path: '/finance/balance-requests/:id/receipt',
    name: 'finance-balance-receipt',
    component: BalanceReceiptView,
    meta: { requiresAuth: true, title: 'Balance Receipt' }
  },

  // Passport routes
  {
    path: '/passports',
    name: 'passports',
    component: PassportsView,
    meta: { requiresAuth: true, title: 'My Visa Status' }
  },
  {
    path: '/passports/:id',
    name: 'passport-details',
    component: PassportDetailsView,
    meta: { requiresAuth: true, title: 'Passport Details' }
  },

  {
    path: '/company',
    redirect: '/company/profile'
  },
  {
    path: '/company/profile',
    name: 'company-profile',
    component: CompanyProfileView,
    meta: { requiresAuth: true, title: 'Company Profile' }
  },
  {
    path: '/company/edit',
    name: 'company-edit',
    component: CompanyEditView,
    meta: { requiresAuth: true, title: 'Edit Company' }
  },
  {
    path: '/users',
    name: 'users',
    component: UsersView,
    meta: { requiresAuth: true, permission: 'view_users', title: 'Users' }
  },
  {
    path: '/users/create',
    name: 'user-create',
    component: UserCreateView,
    meta: { requiresAuth: true, permission: 'add_users', title: 'Create User' }
  },
  {
    path: '/users/:id/edit',
    name: 'user-edit',
    component: UserEditView,
    meta: { requiresAuth: true, permission: 'edit_users', title: 'Edit User' }
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: ChangePasswordView,
    meta: { requiresAuth: true, title: 'Change Password' }
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: NotificationsView,
    meta: { requiresAuth: true, title: 'Notifications' }
  },
  {
    path: '/chat',
    name: 'chat',
    component: ChatView,
    meta: { requiresAuth: true, title: 'Chat' }
  },
  {
    path: '/search',
    name: 'search',
    component: SearchView,
    meta: { requiresAuth: true, title: 'Search' }
  },

  // 404 Not Found
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { title: 'Page Not Found' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to: RouteLocationNormalized, _from: RouteLocationNormalized, savedPosition: { left: number; top: number } | null) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach(async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const authStore = useAuthStore()
  
  // Set page title
  document.title = to.meta.title 
    ? `${to.meta.title} - B2B Tour Deal` 
    : 'B2B Tour Deal'

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Try to restore session from token
      const token = localStorage.getItem('auth_token')
      if (token) {
        try {
          await authStore.fetchUser()
        } catch {
          // Token invalid, redirect to login
          return next({ name: 'login', query: { redirect: to.fullPath } })
        }
      } else {
        return next({ name: 'login', query: { redirect: to.fullPath } })
      }
    }

    // Check permissions if required
    if (to.meta.permission && !authStore.hasPermission(to.meta.permission as string)) {
      return next({ name: 'home' })
    }
  }

  // Redirect authenticated users away from guest-only pages
  if (to.meta.guest && authStore.isAuthenticated) {
    return next({ name: 'home' })
  }

  next()
})

export default router

// Type augmentation for route meta
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    guest?: boolean
    permission?: string
    title?: string
  }
}
